import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { FileResponse, PositionResponse } from "@patent-studio/api-types";
import { commitNovelty, runGate } from "@/services/novelty";
import { usePositions } from "@/hooks/use-positions";
import { useTask } from "@/hooks/use-tasks";
import { priorArtOf } from "@/v2/novelty/prior-art-list";
import { MUTATION_KEYS } from "@/lib/analytics/mutation-reporting";
import {
  claimStrategyKey,
  noveltyGateKey,
  noveltyGateScope,
} from "./query-keys";

/** What the gate reads, as one string, so a change in any of it is a change of key. */
export function gateInputs(
  files: readonly FileResponse[],
  positions: readonly PositionResponse[],
): string {
  const documents = files
    .map(
      (f) => `${f.file_id}:${f.processing_status}:${f.literature_type ?? ""}`,
    )
    .join("|");
  const arguments_ = positions
    .map((p) => `${p.position_id}:${p.confirmed ? 1 : 0}`)
    .join("|");
  return `${documents}#${arguments_}`;
}

/**
 * The served gate. The server owns the rule; the screen owns knowing when
 * its inputs moved, which the key carries.
 */
export function useNoveltyGate(
  taskId: string,
  files: readonly FileResponse[],
  positions: readonly PositionResponse[],
) {
  return useQuery({
    queryKey: noveltyGateKey(taskId, gateInputs(files, positions)),
    queryFn: () => runGate(taskId),
  });
}

/**
 * The served gate for a task, reading for itself the two things it is keyed by.
 *
 * Six screens ask for this gate and every one must key it identically or the
 * strip, the rail, the footers and the guards hold different answers to the same
 * question and fetch it twice. That agreement was six hand-written copies of the
 * same three reads, held together by a comment on each. It is one call now.
 */
export function useNoveltyGateFor(taskId: string) {
  const { data: task } = useTask(taskId);
  const { data: positions } = usePositions(taskId);
  return useNoveltyGate(
    taskId,
    priorArtOf(task?.files),
    positions?.positions ?? [],
  );
}

/**
 * Carry the analysis into Claim Strategy. Every gate the page holds is asked
 * again afterwards, and the claim strategy stage is read fresh before it is
 * entered.
 */
export function useCommitNovelty(taskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.noveltyCommit,
    mutationFn: () => commitNovelty(taskId),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: noveltyGateScope(taskId) }),
        queryClient.invalidateQueries({ queryKey: claimStrategyKey(taskId) }),
      ]);
    },
  });
}
